import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { Button } from './ui/button';

const navLinks = [
  { name: 'About', href: '#about' },
  { 
    name: 'Services', 
    href: '#services',
    hasDropdown: true,
    dropdownItems: [
      { name: 'Immigration Services', href: '/services/immigration', icon: Globe },
      { name: 'All Services', href: '#services' }
    ]
  },
  { name: 'Why Us', href: '#why-us' },
  { name: 'Industries', href: '#industries' },
  { name: 'Process', href: '#process' },
  { name: 'Contact', href: '#contact' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    
    if (href.startsWith('/')) {
      navigate(href);
      return;
    }
    
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDropdownClick = (linkName) => {
    setActiveDropdown(activeDropdown === linkName ? null : linkName);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="fixed top-4 left-4 right-4 z-50 mx-auto max-w-7xl rounded-2xl transition-all duration-300"
        data-testid="navbar"
        style={{
          background: isScrolled 
            ? 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(10,25,47,0.15) 45%, rgba(217,48,37,0.18) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.88) 0%, rgba(10,25,47,0.12) 45%, rgba(217,48,37,0.15) 100%)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          boxShadow: isScrolled ? '0 8px 32px rgba(10,25,47,0.12)' : '0 4px 16px rgba(0,0,0,0.06)',
          border: '1px solid rgba(10,25,47,0.1)'
        }}
      >
        <div className="flex items-center justify-between px-6 py-4">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-1 font-heading font-bold text-xl text-[#0a192f]"
            data-testid="logo"
          >
            Redline<span className="text-[#D93025]">.</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <div key={link.name} className="relative">
                {link.hasDropdown ? (
                  <>
                    <button
                      onClick={() => handleDropdownClick(link.name)}
                      onMouseEnter={() => setActiveDropdown(link.name)}
                      className="font-body text-sm font-medium text-[#64748b] hover:text-[#0a192f] transition-colors flex items-center gap-1"
                      data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {activeDropdown === link.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-[#e2e2dc] overflow-hidden"
                          onMouseLeave={() => setActiveDropdown(null)}
                          data-testid="services-dropdown"
                        >
                          {link.dropdownItems.map((item, index) => (
                            <a
                              key={item.name}
                              href={item.href}
                              onClick={(e) => {
                                e.preventDefault();
                                scrollToSection(item.href);
                              }}
                              className={`flex items-center gap-3 px-4 py-3 text-sm text-[#64748b] hover:text-[#0a192f] hover:bg-[#f5f5f0] transition-colors ${
                                index === 0 ? 'border-b border-[#e2e2dc]' : ''
                              }`}
                              data-testid={`dropdown-${item.name.toLowerCase().replace(' ', '-')}`}
                            >
                              {item.icon && <item.icon className="w-4 h-4 text-[#D93025]" />}
                              {item.name}
                            </a>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className="font-body text-sm font-medium text-[#64748b] hover:text-[#0a192f] transition-colors"
                    data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button
              onClick={() => scrollToSection('#contact')}
              className="bg-[#0a192f] text-white rounded-xl px-6 py-2.5 font-medium hover:bg-[#0a192f]/90 transition-all hover:scale-[1.02] shadow-lg shadow-[#0a192f]/20"
              data-testid="nav-cta-button"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-[#0a192f]"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed top-24 left-4 right-4 z-40 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-[#e2e2dc] p-6 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.hasDropdown ? (
                    <>
                      <button
                        onClick={() => handleDropdownClick(link.name)}
                        className="font-body text-base font-medium text-[#64748b] hover:text-[#0a192f] transition-colors py-2 flex items-center justify-between w-full"
                      >
                        {link.name}
                        <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === link.name && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="pl-4 border-l-2 border-[#D93025] ml-2 overflow-hidden"
                          >
                            {link.dropdownItems.map((item) => (
                              <a
                                key={item.name}
                                href={item.href}
                                onClick={(e) => {
                                  e.preventDefault();
                                  scrollToSection(item.href);
                                }}
                                className="flex items-center gap-2 py-2 text-sm text-[#64748b] hover:text-[#0a192f] transition-colors"
                              >
                                {item.icon && <item.icon className="w-4 h-4 text-[#D93025]" />}
                                {item.name}
                              </a>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.href);
                      }}
                      className="font-body text-base font-medium text-[#64748b] hover:text-[#0a192f] transition-colors py-2 block"
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                    </a>
                  )}
                </div>
              ))}
              <Button
                onClick={() => scrollToSection('#contact')}
                className="bg-[#0a192f] text-white rounded-xl px-6 py-3 font-medium hover:bg-[#0a192f]/90 transition-all w-full mt-2"
                data-testid="mobile-cta-button"
              >
                Get Started
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
